import { EmptySpaceFiller, GroupLabel } from './SharedGroupRuleElements';
import { TextInput } from '@ardoq/forms';
import { Select } from '@ardoq/select';
import {
  GetInvalidGroupingSelectOptions,
  ParentGroupingRule,
  PerspectiveGroupingRule,
  PerspectivesGroupsComponentTypes,
  PerspectivesGroupsWorkspace,
} from '../types';
import { toComponentTypeOption } from './toOptionsMapper';
import { ArdoqId, GroupType } from '@ardoq/api-types';
import { getWorkspaceOptions } from './getWorkspaceOptions';
import { DisabledOptionProps } from '../../types';
import { isTruthy } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

type ParentGroupRuleProps = {
  groupingRule: ParentGroupingRule;
  workspaces: PerspectivesGroupsWorkspace[];
  componentTypesPerWorkspace: Record<
    ArdoqId,
    PerspectivesGroupsComponentTypes[]
  >;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  tabIndex?: number;
  disabledOptionProps?: DisabledOptionProps;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  i18nLocale: I18nLocale;
};

const ParentGroupRule = ({
  workspaces,
  groupingRule,
  componentTypesPerWorkspace,
  onGroupingRuleUpdated,
  disabledOptionProps,
  getInvalidGroupingSelectOptions,
  tabIndex,
  i18nLocale,
}: ParentGroupRuleProps) => {
  const { workspaceSelectOption, ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const workspaceOptions = getWorkspaceOptions({
    workspaces,
    disabledOptionProps,
    entitiesName: 'components',
    entitiesPerWorkspace: componentTypesPerWorkspace,
    firstSelectOption: workspaceSelectOption,
  });

  const componentTypes =
    componentTypesPerWorkspace[groupingRule.workspaceId!] ?? [];
  const componentTypeOptions = [
    ruleTargetSelectOption,
    ...componentTypes.map(toComponentTypeOption),
  ].filter(isTruthy);

  const updateGroupRule = (partialGroupingRule: Partial<ParentGroupingRule>) =>
    onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <GroupLabel data-testid={`group-by-${GroupType.PARENT}-label`}>
        {t(i18nLocale, 'perspectives:groupingRules.parentLabel', 'Parent')}
      </GroupLabel>
      <Select
        dataTestId="parent-grouping-rule-workspace-select"
        value={groupingRule.workspaceId}
        options={workspaceOptions}
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.workspaceAriaLabel',
          'Workspace'
        )}
        tabIndex={tabIndex}
        onChange={event => {
          const nextWorkspaceId = event?.value;
          if (!nextWorkspaceId) return;
          const defaultComponentTypeId =
            componentTypesPerWorkspace[nextWorkspaceId]?.[0]?.id;
          updateGroupRule({
            workspaceId: nextWorkspaceId,
            targetId: defaultComponentTypeId,
          });
        }}
      />

      <Select
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.componentTypesAriaLabel',
          'Component types'
        )}
        dataTestId="parent-grouping-rule-component-type-select"
        options={componentTypeOptions}
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            targetId: event?.value,
          })
        }
      />

      <EmptySpaceFiller />

      <TextInput
        value={groupingRule.sublabel}
        dataTestId="parent-grouping-rule-sublabel-input"
        tabIndex={tabIndex}
        onValueChange={value =>
          updateGroupRule({
            sublabel: value,
          })
        }
      />
    </>
  );
};

export { ParentGroupRule };
